import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, post } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { money } from "@/lib/plans";
import { Button } from "@/components/ui/button";

export const LowTokensWarning = ({ t, testId = "tokens-low-warning" }) => {
  if (!t || !t.texts_on || !t.low) return null;
  return (
    <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800" role="alert" data-testid={testId}>
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      {t.balance === 0
        ? "You've run out of text tokens, so locksmiths won't get run texts. Emails still send. Buy more on the Billing page."
        : `Only ${t.balance} text token${t.balance === 1 ? "" : "s"} left. Buy more on the Billing page so run texts keep sending.`}
    </p>
  );
};

function useTokenReturn(onPaid) {
  const [params, setParams] = useSearchParams();
  const [checking, setChecking] = useState(false);
  const done = useRef(false);
  const sessionId = params.get("tokens_session_id");
  useEffect(() => {
    if (!sessionId || done.current) return undefined;
    done.current = true;
    let tries = 0;
    setChecking(true);
    const poll = async () => {
      try {
        const s = await get(`/sms-tokens/status/${sessionId}`);
        if (s.payment_status === "paid") {
          toast.success(`Payment received. ${s.texts} texts added.`);
          setParams({}, { replace: true });
          setChecking(false);
          onPaid();
          return;
        }
      } catch {
        // retry below
      }
      if (++tries < 15) setTimeout(poll, 2000);
      else {
        setChecking(false);
        toast.error("We couldn't confirm the payment yet. Refresh in a minute to see your balance.");
      }
    };
    poll();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  return checking;
}

const BundleCard = ({ b, busy, onBuy }) => (
  <div className="flex flex-col justify-between gap-3 rounded-lg border border-slate-200 p-4" data-testid={`token-bundle-${b.id}`}>
    <div>
      <p className="font-semibold text-slate-900">{b.name}</p>
      <p className="font-display mt-1 text-2xl font-extrabold text-slate-900">{money(b.price_pence / 100)}</p>
      <p className="text-xs text-slate-500">{b.texts} texts · {(b.price_pence / b.texts).toFixed(1)}p per text</p>
    </div>
    <Button onClick={() => onBuy(b)} disabled={!!busy} className="bg-blue-700 hover:bg-blue-800" data-testid={`token-bundle-buy-${b.id}`}>
      {busy === b.id && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Buy
    </Button>
  </div>
);

const History = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="token-purchase-history">
      <thead><tr className="text-left text-xs text-slate-500"><th className="py-2 pr-3 font-medium">Date</th><th className="py-2 pr-3 font-medium">Bundle</th><th className="py-2 pr-3 font-medium">Texts</th><th className="py-2 pr-3 font-medium">Paid</th><th className="py-2 font-medium">Bought by</th></tr></thead>
      <tbody>
        {rows.map((p) => (
          <tr key={p.id} className="border-t border-slate-100" data-testid={`token-purchase-${p.id}`}>
            <td className="py-2 pr-3">{p.paid_at ? formatDate(p.paid_at.slice(0, 10), "dd/MM/yyyy") : "—"}</td>
            <td className="py-2 pr-3">{p.bundle_name}</td>
            <td className="py-2 pr-3 font-data">{p.texts}</td>
            <td className="py-2 pr-3 font-data">{money(p.amount)}</td>
            <td className="py-2">{p.bought_by}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const TextTokens = () => {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => get("/sms-tokens").then(setT).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
  }, []);
  const checking = useTokenReturn(load);

  const buy = async (b) => {
    setBusy(b.id);
    try {
      const res = await post("/sms-tokens/checkout", { bundle_id: b.id, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(errorMessage(e));
      setBusy(null);
    }
  };

  return (
    <section className="max-w-4xl space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="text-tokens">
      <div className="flex items-start gap-3">
        <MessageSquareText className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Text tokens</h2>
          <p className="text-sm text-slate-600">Each run text to a locksmith (assigned, changed or cancelled) uses 1 token. Emails are always free.</p>
        </div>
      </div>
      {checking && <p className="flex items-center gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800" data-testid="tokens-checking"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…</p>}
      {!t ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <>
          <div>
            <p className="font-display text-4xl font-extrabold text-slate-900" data-testid="tokens-balance">{t.balance}</p>
            <p className="text-sm text-slate-500">texts left</p>
          </div>
          <LowTokensWarning t={t} testId="billing-tokens-low-warning" />
          {!t.texts_on && <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600" data-testid="tokens-texts-off">Run texts are switched off for everyone right now, so no tokens are being used.</p>}
          {t.bundles.length ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{t.bundles.map((b) => <BundleCard key={b.id} b={b} busy={busy} onBuy={buy} />)}</div>
          ) : <p className="text-sm text-slate-500" data-testid="tokens-no-bundles">No text bundles are on sale right now.</p>}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-slate-900">Purchase history</h3>
            {t.purchases.length ? <History rows={t.purchases} /> : <p className="text-sm text-slate-500" data-testid="tokens-no-purchases">No purchases yet.</p>}
          </div>
        </>
      )}
    </section>
  );
};
